import axios, { type AxiosResponse } from "axios";
import { GetUserTestsResponse } from "p6m-aea-networking";
import {
    StudentTestAnswer,
    StudentTestContent,
    StudentTestContentWrapper,
    TeacherTestDetails,
    UnitCardsResponse,
} from "p6m-p6u";
import { deleteRequest, getRequest, postRequest, putRequest } from "../helpers/networkHelper";
import {
    extractAllResults,
    extractResultsForMember,
    extractClassMemberActivities,
    extractClassMemberVocabActivities,
    pickRelevantClassMemberResult,
    type ClassMemberActivity,
    type ClassMemberClassTestRaw,
    type ClassMemberTestResultRow,
    type ClassMemberVocabActivity,
    type ClassMemberVocabTrainingRaw,
} from "../helpers/ClassMemberActivities";
import { toEpochMs } from "../utils/dateUtils";
import { NetworkErrorCodes } from "./errorCodes";

export interface VocabTrainingMetadataPayload {
    testUuid: string;
    teacherId: string;
    articleUuid: string | null; // UUID of the source book. null for own-content trainings.
    schoolClassId: string | null;
    // Stringified `{ ownSubjectInfo }` for own-content trainings — what recent-articles keys off in place of
    // articleUuid, mirroring the json column of tests. Absent for book trainings.
    json?: string;
    testName: string;
    bookName: string;
    vocabularyCount: number;
    shareLink: string;
    // null = HIDDEN, <= now = VISIBLE, > now = PLANNED. Canonical source FE-side.
    // FE always writes epoch ms; string (ISO) only occurs on legacy records read back from BE.
    visibilityDate: number | string | null;
}

/**
 * Registers a freshly created vocab training so the backend surfaces it in aea/recent-articles/.
 *
 * ArticleUuid + teacherId are what recently used content keys off. Omitting them still returns 200,
 * so a partial payload breaks the feature silently.
 */
export const shareVocabTrainingMetadata = async (payload: VocabTrainingMetadataPayload): Promise<void> => {
    await postRequest("aea/vocabulary-training-metadata/", payload);
};

/**
 * Deleting the whole training is the only direct use. To clear a schoolClass, go through
 * {@link recreateVocabTrainingMetadataWithoutSchoolClass} — the row is meant to outlive
 * schoolClass changes.
 */
export const deleteVocabTrainingMetadata = async (testUuid: string): Promise<void> => {
    await deleteRequest("aea/vocabulary-training-metadata/", { testUuid });
};

/**
 * Clears a training's schoolClass by deleting the metadata row and re-inserting it without one:
 * the upsert silently ignores `schoolClassId: null` (other nullable fields, `visibilityDate`
 * among them, are honored), so this is the only FE-side way to clear it. Drop this dance once the
 * BE accepts a null schoolClass — every delete is a window where the row doesn't exist.
 *
 * The fresh row's visibility is exactly the payload's `visibilityDate`. Throws if either leg fails —
 * a failed delete leaves the old schoolClass in place, so callers must not report the ROW as cleared.
 * (updateVocabTrainingSchoolClass still resolves true on failure, because its boolean reports the pxp
 * write rather than the row — see the comment there.)
 */
export const recreateVocabTrainingMetadataWithoutSchoolClass = async (
    payload: VocabTrainingMetadataPayload
): Promise<void> => {
    try {
        await deleteVocabTrainingMetadata(payload.testUuid);
    } catch (e) {
        // Trainings whose creation-time registration failed have no row to delete. Today that answers
        // 200 like any other delete; this only guards the conventional 404 should the BE ever send it.
        if (!(axios.isAxiosError(e) && e.response?.status === NetworkErrorCodes.NOT_FOUND)) throw e;
    }
    await shareVocabTrainingMetadata({ ...payload, schoolClassId: null });
};

/**
 * Resolves `undefined` for a missing row. The BE answers 200 with a `null` body (not 404) for
 * absent rows; the 404 branch is only a guard should that ever change.
 */
export const getVocabTrainingMetadata = async (testUuid: string): Promise<VocabTrainingMetadataPayload | undefined> => {
    try {
        const response: AxiosResponse<VocabTrainingMetadataPayload | null> = await getRequest(
            "aea/vocabulary-training-metadata/",
            { testUuid }
        );
        return response.data ?? undefined;
    } catch (e) {
        if (axios.isAxiosError(e) && e.response?.status === NetworkErrorCodes.NOT_FOUND) return undefined;
        throw e;
    }
};

// Per-training failures are swallowed so one bad row doesn't blank the whole map. Failed IDs are
// returned so the caller can log/surface partial failures rather than silently falling back to HIDDEN.
export interface AllVocabTrainingMetadataResult {
    data: Record<string, VocabTrainingMetadataPayload>;
    failedIds: string[];
}

export const getAllVocabTrainingMetadata = async (testUuids: string[]): Promise<AllVocabTrainingMetadataResult> => {
    const data: Record<string, VocabTrainingMetadataPayload> = {};
    const failedIds: string[] = [];
    await Promise.all(
        testUuids.map(async (testUuid) => {
            try {
                const payload = await getVocabTrainingMetadata(testUuid);
                if (payload) data[testUuid] = payload;
            } catch {
                failedIds.push(testUuid);
            }
        })
    );
    return { data, failedIds };
};

/**
 * Deletes a test, treating an already-absent one as success — the desired end state either way. The BE
 * answers 404 for a missing test (verified 2026-08-11), so without this a class whose test vanished
 * mid-cleanup — concurrent delete, or a retry after a partial failure — could never be deleted: every
 * attempt would re-fail on the same missing test.
 */
export const deleteTestToleratingMissing = async (testId: string): Promise<void> => {
    try {
        await deleteRequest(`aea/tests/?testId=${testId}`);
    } catch (e) {
        if (!(axios.isAxiosError(e) && e.response?.status === NetworkErrorCodes.NOT_FOUND)) throw e;
    }
};

// Rejects on failure. Callers delete based on which tests are missing from this list, so an empty result
// must mean "no tests" and never "the lookup failed" — read paths handle the rejection themselves.
export const getUserTests = async (): Promise<Array<TeacherTestDetails>> => {
    const userTestsList: Array<TeacherTestDetails> = [];

    const endpointResponse: AxiosResponse<GetUserTestsResponse> = await getRequest("aea/tests/");
    Object.entries(endpointResponse.data).forEach(([id, testContent]) => {
        userTestsList.push({ ...testContent, id });
    });
    // toEpochMs keeps legacy ISO-string dates from turning the subtraction into NaN.
    userTestsList.sort((t1, t2) => (toEpochMs(t2.updatedDate) ?? 0) - (toEpochMs(t1.updatedDate) ?? 0));

    return userTestsList;
};

export const getCardsForUnit = async (unitId: string): Promise<UnitCardsResponse> => {
    let cardsForUnit: UnitCardsResponse = { count: 0, items: [] };

    try {
        const endpointResponse: AxiosResponse<UnitCardsResponse> = await getRequest("aea/cdb-cards/", { unitId });
        cardsForUnit = endpointResponse.data;
    } catch (e: any) {
        if (e.response.status === NetworkErrorCodes.FORBIDDEN) {
            console.log("forbidden");
            return Promise.reject();
        }
        console.log(e.response.status);
        console.log("Error while getting tests");
    }

    return cardsForUnit;
};

export const getCardsByIds = async (cardIds: string[]): Promise<UnitCardsResponse> => {
    const endpointResponse: AxiosResponse<UnitCardsResponse> = await postRequest("aea/cdb-cards/", { cardIds });
    return endpointResponse.data;
};

export const getStudentTestById = async (testId: string): Promise<StudentTestContent | undefined> => {
    try {
        const studentTestDataResponse: AxiosResponse<StudentTestContent> = await getRequest(
            `aea/student-tests/?testId=${testId}`
        );
        const studentTestData = { ...studentTestDataResponse.data, id: testId };
        return Promise.resolve(studentTestData);
    } catch (e: any) {
        if (e.response.status === NetworkErrorCodes.NOT_FOUND) {
            return Promise.resolve(undefined);
        } else {
            console.log(e);
            return Promise.reject(e);
        }
    }
};

// Rejects on failure. Callers delete based on what is missing from this list, so an empty result must mean
// "nothing is shared" and never "the lookup failed" — the two are indistinguishable once it resolves.
export const getTeacherSharedTests = async (): Promise<StudentTestContentWrapper> => {
    const teacherSharedTests: StudentTestContentWrapper = {};
    const teacherSharedTestsResponse: AxiosResponse<StudentTestContentWrapper> = await getRequest(`aea/student-tests/`);
    // Making sure the ID property exists in there.
    Object.entries(teacherSharedTestsResponse.data).forEach(([id, test]) => {
        teacherSharedTests[id] = { ...test, id };
    });
    return teacherSharedTests;
};

/*
 * The list is looked up per member (private classes) or per class token (public classes,
 * which have no logged-in member) — both go through the same aea/student-tests-class/ endpoint.
 */
export type ClassMemberActivitiesSource = { classMemberId: string } | { classToken: string };

/**
 * Fetches the tests/practices shared with a class for the student dashboard. Keyed by classMemberId
 * for private classes or classToken for public ones.
 */
export const getClassMemberActivitiesForClass = async (
    source: ClassMemberActivitiesSource
): Promise<ClassMemberActivity[]> => {
    const response: AxiosResponse<Record<string, ClassMemberClassTestRaw>> = await getRequest(
        "aea/student-tests-class/",
        source
    );

    return extractClassMemberActivities(response.data || {});
};

/**
 * Vocab metadata is class-scoped by token only (no member-scoped variant), which works for both
 * class kinds since the dashboard knows its class code either way.
 */
export const getClassMemberVocabTrainingsForClass = async (classToken: string): Promise<ClassMemberVocabActivity[]> => {
    const response: AxiosResponse<ClassMemberVocabTrainingRaw[]> = await getRequest(
        "aea/vocabulary-training-metadata/class/",
        { classToken }
    );

    // The endpoint may answer with "" instead of [] when there is nothing to list.
    return extractClassMemberVocabActivities(response.data || []);
};

/**
 * Fetches the current class member's result for a single test. The classMemberId param is required:
 * without it the endpoint demands a teacher session and answers 400 for logged-out class members.
 * The member results are still filtered client-side as well, since the rows arrive as json blobs
 * that need parsing anyway.
 */
export const getTestResultForClassMember = async (
    testId: string,
    classMemberId: string
): Promise<StudentTestAnswer | null> => {
    const response: AxiosResponse<Record<string, ClassMemberTestResultRow>> = await getRequest(
        `aea/student-tests/results/?testId=${testId}&classMemberId=${classMemberId}`
    );

    // The endpoint answers with "" instead of {} when there are no results.
    const ownResults = extractResultsForMember(response.data || {}, classMemberId);

    return pickRelevantClassMemberResult(ownResults);
};

/** Needs the teacher session: without a classMemberId filter the endpoint rejects class-member sessions. */
export const getTestResultsForTest = async (testId: string): Promise<StudentTestAnswer[]> => {
    const response: AxiosResponse<Record<string, ClassMemberTestResultRow>> = await getRequest(
        `aea/student-tests/results/?testId=${testId}`
    );
    return extractAllResults(response.data || {});
};

export const saveStudentTestResult = async (
    testId: string,
    resultId: string,
    content: StudentTestAnswer
): Promise<boolean> => {
    try {
        await putRequest(`aea/student-tests/results/?testId=${testId}&resultId=${resultId}`, { ...content });
        return Promise.resolve(true);
    } catch (e: any) {
        console.log(e);
        return Promise.reject(e);
    }
};

/** The result's owner is taken from content.classMemberId, as in the pupil's own submit. */
export const createClassMemberTestResult = async (testId: string, content: StudentTestAnswer): Promise<void> => {
    await postRequest(`aea/student-tests/results/?testId=${testId}`, { ...content });
};

export interface SubmitStudentTestResultOptions {
    isPreview: boolean;
    isOwner: boolean;
}

// Defense-in-depth: the UI already routes owner/preview interactions to a close action, but this
// guard catches any code path that still funnels into a POST. Hitting it is a programmer error —
// surface loudly and skip the request rather than polluting the test's submissions list.
export const submitStudentTestResult = async (
    testId: string,
    content: StudentTestAnswer,
    options: SubmitStudentTestResultOptions
): Promise<void> => {
    if (options.isPreview || options.isOwner) {
        console.error(
            "submitStudentTestResult called in preview/owner mode — refusing to POST. " +
                "This indicates an unguarded submission path in the UI.",
            { testId, isPreview: options.isPreview, isOwner: options.isOwner }
        );
        return;
    }
    await createClassMemberTestResult(testId, content);
};
